import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Cliente API para el sistema de campos dinámicos
 * Todas las funciones requieren un token de autenticación
 */

// ========================================
// CONFIGURACIÓN DE CAMPOS POR ESTADO
// ========================================

/**
 * Obtener todos los campos asignados a un estado específico de una empresa
 */
export const obtenerCamposPorEstado = async (id_empresa, id_estado, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/empresa/${id_empresa}/estado/${id_estado}/campos`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Obtener todos los estados con sus campos configurados para una empresa
 */
export const obtenerTodosCamposPorEmpresa = async (id_empresa, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/empresa/${id_empresa}/campos-estados`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Asignar múltiples campos a un estado
 * @param {number} id_empresa
 * @param {number} id_estado
 * @param {Array} campos - Array de { id_campo, es_obligatorio, orden_visualizacion, configuracion_override }
 * @param {string} token
 */
export const asignarCamposAEstado = async (id_empresa, id_estado, campos, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/empresa/${id_empresa}/estado/${id_estado}/campos`,
    { campos },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Actualizar configuración de un campo asignado
 */
export const actualizarCampoEstado = async (id_asignacion, datos, token) => {
  const response = await axios.put(
    `${API_BASE_URL}/api/v1/empresa-campos/${id_asignacion}`,
    datos,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Eliminar (desactivar) un campo de un estado
 */
export const eliminarCampoEstado = async (id_asignacion, token) => {
  const response = await axios.delete(
    `${API_BASE_URL}/api/v1/empresa-campos/${id_asignacion}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Reordenar campos de un estado
 * @param {Array} ordenCampos - Array de { id_campo, orden_visualizacion }
 */
export const reordenarCampos = async (id_empresa, id_estado, ordenCampos, token) => {
  const response = await axios.put(
    `${API_BASE_URL}/api/v1/empresa/${id_empresa}/estado/${id_estado}/campos/reordenar`,
    { ordenCampos },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

// ========================================
// VALORES DE CAMPOS POR ORDEN
// ========================================

/**
 * Obtener valores de campos de una orden en un estado específico
 */
export const obtenerValoresPorOrdenEstado = async (id_orden, id_estado, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/orden/${id_orden}/estado/${id_estado}/valores`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Obtener historial completo de valores de una orden (todos los estados)
 */
export const obtenerHistorialCompleto = async (id_orden, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/orden/${id_orden}/historial-valores`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Guardar valores de campos para una orden en un estado específico
 * @param {Array} valores - Array de { id_campo, valor }
 */
export const guardarValores = async (id_orden, id_estado, valores, token) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/orden/${id_orden}/estado/${id_estado}/valores`,
    { valores },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Actualizar un valor específico
 */
export const actualizarValor = async (id_valor, valor, token) => {
  const response = await axios.put(
    `${API_BASE_URL}/api/v1/valor/${id_valor}`,
    { valor },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

/**
 * Eliminar valores de una orden en un estado específico
 */
export const eliminarValoresPorOrdenEstado = async (id_orden, id_estado, token) => {
  const response = await axios.delete(
    `${API_BASE_URL}/api/v1/orden/${id_orden}/estado/${id_estado}/valores`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

// ========================================
// TESTING Y UTILIDADES
// ========================================

/**
 * Verificar estado del sistema de campos dinámicos
 */
export const verificarHealth = async () => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/test-campos/health`
  );
  return response.data;
};

/**
 * Simular validación de cambio de estado
 */
export const simularValidacion = async (id_orden, id_estado_origen, id_estado_destino, id_empresa) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/test-campos/simular-validacion`,
    {
      id_orden,
      id_estado_origen,
      id_estado_destino,
      id_empresa
    }
  );
  return response.data;
};

/**
 * Obtener estado detallado de campos de una orden
 */
export const obtenerEstadoCampos = async (id_orden, id_estado, id_empresa, token) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/test-campos/estado-campos/${id_orden}/${id_estado}/${id_empresa}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data;
};

export default {
  // Configuración
  obtenerCamposPorEstado,
  obtenerTodosCamposPorEmpresa,
  asignarCamposAEstado,
  actualizarCampoEstado,
  eliminarCampoEstado,
  reordenarCampos,

  // Valores
  obtenerValoresPorOrdenEstado,
  obtenerHistorialCompleto,
  guardarValores,
  actualizarValor,
  eliminarValoresPorOrdenEstado,

  // Testing
  verificarHealth,
  simularValidacion,
  obtenerEstadoCampos
};
